import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns          as getCampaigns,
  getDashboardAbandonment        as getAbandonment,
  getDashboardAbandonmentStages  as getAbandonmentStages,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";
const GRAY   = "#888780";

const STAGE_COLORS = {
  "After Initial Text":      BLUE,
  "After AI Disclosure":     "#185FA5",
  "After Identity Check":    GREEN,
  "Before Customer Offer":   AMBER,
  "After Customer Offer":    PINK,
  "After Amount Agreement":  PURPLE,
  "At Payment Confirmation": GRAY,
};

const GRAN_OPTIONS = ["Day", "Week", "Month"];

// ── Date range presets ─────────────────────────────────────────────
const RANGE_OPTIONS = [
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  };
  return {
    startDate: toLocal(start),
    endDate:   toLocal(end),
  };
}

// Previous period = same duration as current, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function fmt(n, dec = 0) {
  const num = Number(n);
  if (n == null || isNaN(num)) return "—";
  return num.toFixed(dec);
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  // new Date("2026-04-16") parses as UTC midnight → shifts back a day
  // in negative-offset timezones. Slashes force local-time parsing.
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// Build a delta object from current/prev numeric values.
//   up:   true if current value increased vs prev  → drives ▲/▼ arrow
//   good: true if the change is favorable          → drives green/red color
//   text: "+5.2% vs 234" — pct change + actual prev value formatted
//
// `fmtVal` formats the prev value for display. Defaults to comma-formatted
// integer (e.g. 1234 → "1,234"). Pass a custom formatter for percentages.
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// Aggregate abandonment rows into KPI scalars. Used for both current and
// prev-period datasets so deltas come from identical math.
function aggregateAbandonment(rows) {
  const responses    = (rows || []).reduce((a,b) => a + safeNum(b.responses),     0);
  const abandon1     = (rows || []).reduce((a,b) => a + safeNum(b.abandon1),      0);
  const abandon2     = (rows || []).reduce((a,b) => a + safeNum(b.abandon2),      0);
  const abandon3     = (rows || []).reduce((a,b) => a + safeNum(b.abandon3),      0);
  const abandonTotal = (rows || []).reduce((a,b) => a + safeNum(b.abandon_total), 0);
  return {
    responses, abandon1, abandon2, abandon3, abandonTotal,
    abandonRate: responses > 0 ? abandonTotal / responses                 * 100 : null,
    completion:  responses > 0 ? (responses - abandonTotal) / responses   * 100 : null,
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none",
                border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition:  "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Abandon funnel ─────────────────────────────────────────────────
function StageFunnel({ data }) {
  const max = Math.max(1, ...data.map(d => safeNum(d.stage_count)));
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {data.map(s => (
        <Box key={s.stage} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            flex: 1, background: "#f0f0f0", borderRadius: "4px",
            height: 26, position: "relative", overflow: "hidden",
          }}>
            <Box sx={{
              height: "100%", borderRadius: "4px",
              width: `${Math.round(safeNum(s.stage_count) / max * 100)}%`,
              background: STAGE_COLORS[s.stage] || BLUE,
              display: "flex", alignItems: "center", pl: 1,
              fontSize: 11, color: "#fff", fontWeight: 500,
              transition: "width .4s ease",
            }}>
              {safeNum(s.stage_count) > 0 ? safeNum(s.stage_count) : ""}
            </Box>
          </Box>
          <Box sx={{ width: 110, textAlign: "right", fontSize: 11, color: "text.secondary" }}>
            <span style={{ fontSize: 10 }}>{s.stage}</span><br />
            <span style={{ color: STAGE_COLORS[s.stage] || BLUE, fontWeight: 500 }}>
              {fmt(safeNum(s.stage_ratio) * 100, 1)}%
            </span>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function AbandonmentDashboard({ channel }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("7D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [abandonment,     setAbandonment]     = useState([]);
  const [stages,          setStages]          = useState([]);
  // Prev-period dataset — drives KPI deltas only, not charted.
  const [prevAbandonment, setPrevAbandonment] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [rangePreset, campaign, gran, channel]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [params.startDate, params.endDate, campaign, gran, channel]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, s, pa] = await Promise.all([
        getAbandonment(params),
        getAbandonmentStages(params),
        // Prev period: failures non-fatal — KPIs just won't show deltas.
        getAbandonment(prevParams).catch(() => []),
      ]);
      setAbandonment(a      || []);
      setStages(s           || []);
      setPrevAbandonment(pa || []);
    } catch (e) {
      console.error("Abandonment load error:", e?.response?.data, e?.message);
      setError(`Failed to load abandonment data: ${e?.response?.data?.error || e?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── KPIs (current values + prev-period deltas) ─────────────────
  // Responses & completion are good-when-up; every abandon metric is bad-when-up.
  const kpis = useMemo(() => {
    const cur  = aggregateAbandonment(abandonment);
    const prev = aggregateAbandonment(prevAbandonment);
    const pct2 = v => `${fmt(v, 2)}%`;
    const pct1 = v => `${fmt(v, 1)}%`;
    return {
      ...cur,
      responsesDelta:    buildDelta(cur.responses,    prev.responses,    { goodWhenUp: true  }),
      abandonTotalDelta: buildDelta(cur.abandonTotal, prev.abandonTotal, { goodWhenUp: false }),
      abandonRateDelta:  buildDelta(cur.abandonRate,  prev.abandonRate,  { goodWhenUp: false, fmtVal: pct2 }),
      abandon1Delta:     buildDelta(cur.abandon1,     prev.abandon1,     { goodWhenUp: false }),
      abandon2Delta:     buildDelta(cur.abandon2,     prev.abandon2,     { goodWhenUp: false }),
      abandon3Delta:     buildDelta(cur.abandon3,     prev.abandon3,     { goodWhenUp: false }),
      completionDelta:   buildDelta(cur.completion,   prev.completion,   { goodWhenUp: true,  fmtVal: pct1 }),
    };
  }, [abandonment, prevAbandonment]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    abandonment.map(r => ({
      date:        fmtDate(r.summary_date),
      responses:   safeNum(r.responses),
      abandon1:    safeNum(r.abandon1),
      abandon2:    safeNum(r.abandon2),
      abandon3:    safeNum(r.abandon3),
      abandonRate: safeNum(r.abandon_rate),
    }))
  , [abandonment]);

  const stageChartData = useMemo(() =>
    stages.map(s => ({
      stage: s.stage?.replace("After ", "").replace("Before ", "Before "),
      count: safeNum(s.stage_count),
      pct:   Math.round(safeNum(s.stage_ratio) * 100),
      full:  s.stage,
    }))
  , [stages]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading abandonment data…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── ABANDONMENT KPIs ──────────────────────────────── */}
          <SectionTitle>{channel === "emails" ? "Email" : "Text"} abandonment</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Total responses"    value={kpis.responses}                 color={BLUE}  delta={kpis.responsesDelta}    />
            <KpiCard label="Total abandonments" value={kpis.abandonTotal}              color={PINK}  delta={kpis.abandonTotalDelta} />
            <KpiCard label="Abandon rate"       value={`${fmt(kpis.abandonRate, 2)}%`} color={PINK}  delta={kpis.abandonRateDelta}  />
            <KpiCard label="Abandon 1 (2 msgs)" value={kpis.abandon1}                  color={AMBER} delta={kpis.abandon1Delta}     />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Abandon 2 (3 msgs)" value={kpis.abandon2} color={AMBER} delta={kpis.abandon2Delta} />
            <KpiCard label="Abandon 3 (4 msgs)" value={kpis.abandon3} color={AMBER} delta={kpis.abandon3Delta} />
            <KpiCard label="Completion"
              value={kpis.completion == null ? "—" : `${fmt(kpis.completion, 1)}%`}
              color={GREEN}
              delta={kpis.completionDelta}
            />
          </Box>

          {/* ── ABANDONMENT OVER TIME ─────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Abandonments over time" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis yAxisId="left"  tick={axisStyle} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ ...axisStyle, fill: AMBER }}
                    tickFormatter={v => `${v}%`} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="abandon1" name="Abandon 1" stackId="a" fill={BLUE}  radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="abandon2" name="Abandon 2" stackId="a" fill={PINK}  radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="abandon3" name="Abandon 3" stackId="a" fill={AMBER} radius={[3,3,0,0]} />
                  <Line yAxisId="right" type="monotone" dataKey="abandonRate"
                    name="Abandon rate %" stroke={PURPLE} dot={{ r: 3 }} strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Responses vs abandonments" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="responses" name="Responses"    fill={BLUE} radius={[3,3,0,0]} />
                  <Bar dataKey="abandon1"  name="Abandonments" fill={PINK} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* ── ABANDONMENT BY STAGE ──────────────────────────── */}
          <SectionTitle>Abandonment by stage</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Stage breakdown — count" height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis type="number" tick={axisStyle} />
                  <YAxis type="category" dataKey="stage"
                    tick={{ fontSize: 9, fill: "#888" }} width={100} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, n, p) => [v, p.payload.full]}
                  />
                  <Bar dataKey="count" name="Count" radius={[0,3,3,0]}>
                    {stageChartData.map((entry, i) => (
                      <Cell key={i} fill={STAGE_COLORS[entry.full] || BLUE} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Stage funnel" height={240}>
              <StageFunnel data={stages} />
            </ChartCard>
          </Box>
        </>
      )}
    </Box>
  );
}